import { BufferAttribute, Intersection, LineSegments, Matrix4, Mesh, Points, Vector3 } from 'three';

import { isDefined } from '../../../utils/variables';
import {
  Conic,
  getConicCenter,
  getConicCircleCenter,
  getConicLength,
  getConicParametersAtLengths,
  getConicPoint,
} from '../../elements/design/design-node/utils/conic';
import {
  isStyledLines,
  StyledLines,
  StyledLinesIntersection,
} from '../../elements/styled-lines/styled-lines';
import { readItem } from '../../elements/styled-lines/styled-lines-data';
import { getIntersectedSegment } from './asset-line';

/**
 * Which point of the hovered geometry is snapped to, instead of the hovered position:
 * - corner: the closest corner, the end of a line or a corner of a triangle.
 * - midpoint: the middle of the line, along it for a curve, or of the closest edge of a triangle.
 * - center: the center of a circle or an ellipse, or of an arc of one.
 * - intersection: where two lines near the cursor cross in the horizontal plane, see
 *   findClosestCrossing. It needs the lines near the cursor, not only the hovered one.
 */
export type SnapStyle = 'corner' | 'midpoint' | 'center' | 'intersection';

/**
 * A snap style that can be turned on, or free: the hovered position itself. Several can be on at
 * the same time, see chooseSnapCandidate.
 */
export type SnapStyleOption = SnapStyle | 'free';

const closestTo = (position: Vector3, points: Vector3[]) =>
  points.reduce((closest, point) =>
    point.distanceTo(position) < closest.distanceTo(position) ? point : closest,
  );

/**
 * Gets the middle of the closest edge of the triangle.
 */
const getClosestEdgeMiddle = (position: Vector3, [a, b, c]: Vector3[]) =>
  closestTo(position, [
    a.clone().add(b).multiplyScalar(0.5),
    b.clone().add(c).multiplyScalar(0.5),
    c.clone().add(a).multiplyScalar(0.5),
  ]);

/**
 * Gets the center of the circle or ellipse that the curve is an arc of.
 *
 * Free-form outlines are also drawn with arcs of ellipses, of a different ellipse each, so an arc
 * that is not circular only has a center when the curves it continues into are arcs of the same
 * ellipse (the other pieces of an ellipse), or when it continues into no other curves.
 */
export function getArcCenter(conic: Conic, connected: Conic[] = []): Vector3 | undefined {
  const center = getConicCenter(conic);

  if (!center || getConicCircleCenter(conic)) {
    return center;
  }

  const tolerance = 1e-3 * conic.start.distanceTo(conic.end);
  return connected.every((x) => (getConicCenter(x)?.distanceTo(center) ?? Infinity) <= tolerance)
    ? center
    : undefined;
}

/**
 * Gets the curves before and after the item that it continues into, in world coordinates unless
 * another matrix is given, e.g. the other pieces of an ellipse. The curves of a path are next to
 * each other in the data.
 */
export function getConnectedCurves(
  lines: StyledLines,
  index: number,
  matrix: Matrix4 = lines.matrixWorld,
): Conic[] {
  const item = readItem(lines.data, index);

  return [index - 1, index + 1]
    .filter((i) => i >= 0 && i < lines.data.count)
    .map((i) => readItem(lines.data, i))
    .filter(
      (x) =>
        x.kind === 'curve' &&
        [x.start, x.end].some((p) => p.equals(item.start) || p.equals(item.end)),
    )
    .map((x) => ({
      start: x.start.applyMatrix4(matrix),
      control: x.control.applyMatrix4(matrix),
      end: x.end.applyMatrix4(matrix),
      weight: x.weight,
    }));
}

/**
 * Gets the point of a line from start to end, or a curve, that the snap style targets. The curves
 * that it continues into decide whether an arc of an ellipse has a center, see getArcCenter.
 */
export function getLineSnapStylePoint(
  style: SnapStyle,
  position: Vector3,
  start: Vector3,
  end: Vector3,
  conic?: Conic,
  connected?: Conic[],
): Vector3 | undefined {
  switch (style) {
    case 'corner':
      return closestTo(position, [start, end]).clone();
    case 'midpoint': {
      if (!conic) {
        return start.clone().add(end).multiplyScalar(0.5);
      }

      const [t] = getConicParametersAtLengths(conic, [getConicLength(conic) / 2]);
      return getConicPoint(conic, t);
    }
    case 'center':
      return conic && getArcCenter(conic, connected);
    case 'intersection':
      return undefined;
  }
}

/**
 * Gets the point of the intersected geometry of an asset that the snap style targets, in world
 * coordinates, or undefined if it has none, e.g. the center of a straight line.
 */
export function getSnapStylePoint(
  intersection: Intersection,
  style: SnapStyle,
): Vector3 | undefined {
  const { object, point } = intersection;

  // A point, e.g. a survey point, is a corner.
  if (object instanceof Points) {
    const position = (object as Points).geometry.getAttribute('position') as BufferAttribute;
    return style === 'corner' && isDefined(intersection.index)
      ? object.localToWorld(new Vector3().fromBufferAttribute(position, intersection.index))
      : undefined;
  }

  if (isStyledLines(object)) {
    const { item, itemIndex } = intersection as StyledLinesIntersection;
    const toWorld = (p: Vector3) => p.clone().applyMatrix4(object.matrixWorld);
    const [start, end, control] = [item.start, item.end, item.control].map(toWorld);

    if (style === 'intersection') {
      return undefined;
    }

    if (item.kind === 'triangle') {
      return style === 'corner'
        ? closestTo(point, [start, end, control]).clone()
        : style === 'midpoint'
          ? getClosestEdgeMiddle(point, [start, end, control])
          : undefined;
    }

    // A dot is its own corner and center.
    if (item.start.equals(item.end)) {
      return style === 'midpoint' ? undefined : start;
    }

    const conic = item.kind === 'curve' ? { start, control, end, weight: item.weight } : undefined;
    const connected = conic && style === 'center' ? getConnectedCurves(object, itemIndex) : [];
    return getLineSnapStylePoint(style, point, start, end, conic, connected);
  }

  if (object instanceof LineSegments) {
    const lines = object as LineSegments;
    const segment = getIntersectedSegment(lines, intersection);
    return segment
      ? getLineSnapStylePoint(
          style,
          point,
          lines.localToWorld(segment[0]),
          lines.localToWorld(segment[1]),
        )
      : undefined;
  }

  if (
    object instanceof Mesh &&
    isDefined(intersection.face) &&
    style !== 'center' &&
    style !== 'intersection'
  ) {
    const mesh = object as Mesh;
    const position = mesh.geometry.getAttribute('position') as BufferAttribute;
    const { a, b, c } = intersection.face;
    const corners = [a, b, c].map((i) =>
      mesh.localToWorld(new Vector3().fromBufferAttribute(position, i)),
    );

    return style === 'corner'
      ? closestTo(point, corners).clone()
      : getClosestEdgeMiddle(point, corners);
  }

  return undefined;
}
